import {
  BroadcastNotificationType,
  IAdminBroadcastGroupListData,
  IBroadcastNotificationsData,
  PermissionsOfAdmin,
  TimeFormat,
} from '@pharmaplan/common';
import {
  BroadcastNotificationMap,
  DynamicTableCell,
} from '../../../helpers/Constants';
import { getDayMonthDateYear, getHourMinute } from '../../Reports/helpers';
import strings from '../../../localization';

import editIcon from '../../../assets/svg/updateNoteIcon.svg';
import deleteIcon from '../../../assets/svg/delete.svg';
import showPassword from '../../../assets/svg/showPassword.svg';

const { text, bar, labelValue, checkbox } = DynamicTableCell;
const { BroadcastNotification } = PermissionsOfAdmin;
const actionKey = 'actionKey';
const selectKey = 'selectKey';

export const broadcastGroupHeaders = (
  can: (param: PermissionsOfAdmin) => boolean,
) => {
  const cannotUseBroadcast = !can(BroadcastNotification);
  let list = [
    { key: '1', label: strings.groupName, sortingKey: 'name' },
    { key: '2', label: `${strings.created} ${strings.date}`, sortingKey: 'createDate' },
    { key: '3', label: `${strings.created} ${strings.by}`, sortingKey: 'userName' },
    { key: actionKey, label: strings.action },
  ];
  if (cannotUseBroadcast) {
    list = list.filter((item) =>
      item.key !== actionKey);
  }
  return list;
};

export const broadcastNotificationHeaders = (
  can: (param: PermissionsOfAdmin) => boolean,
) => {
  const cannotUseBroadcast = !can(BroadcastNotification);

  let list = [
    { key: '1', label: strings.notificationType, sortingKey: 'type' },
    { key: '2', label: `${strings.sent} ${strings.date} | ${strings.time}`, sortingKey: 'sentDate' },
    { key: '3', label: strings.notifications },
    { key: '4', label: strings.group },
    { key: actionKey, label: strings.action },
    { key: selectKey, label: strings.deleteRecords },
  ];
  if (cannotUseBroadcast) {
    list = list.filter((item) =>
      ![actionKey, selectKey].includes(item.key));
  }
  return list;
};

export const broadcastGroupRows = (
  data: Array<IAdminBroadcastGroupListData>,
  handleEdit: (groupId: string, groupName: string) => () => void,
  handleDelete: (groupId: string) => () => void,
  can: (permission: PermissionsOfAdmin) => boolean,
  showDetails: (groupId: string) => () => void,
) =>
  data?.map((item) => {
    const { name, userName, createDate, broadcastGroupId } = item ?? {};

    const creationDate = getDayMonthDateYear(createDate);
    const canUseBroadcast = can(BroadcastNotification);

    return {
      key: broadcastGroupId,
      data: [
        {
          key: '1',
          value: name,
          type: text,
        },
        {
          key: '2',
          value: creationDate,
          type: text,
        },
        {
          key: '3',
          value: userName,
          type: text,
        },
        {
          key: '4',
          value: [
            canUseBroadcast && {
              key: '1',
              icon: editIcon,
              tooltip: strings.edit,
              onClick: handleEdit(broadcastGroupId, name),
            },
            canUseBroadcast && {
              key: '2',
              icon: deleteIcon,
              tooltip: strings.delete,
              onClick: handleDelete(broadcastGroupId),
            },
            canUseBroadcast && {
              key: '3',
              icon: showPassword,
              tooltip: strings.viewDetails,
              onClick: showDetails(broadcastGroupId),
            },
          ],
          type: bar,
        },
      ],
    };
  });

export const broadcastNotificationRows = (
  data: Array<IBroadcastNotificationsData>,
  timeFormat: TimeFormat,
  handleEdit: (
    id: string,
    notificationType: BroadcastNotificationType
  ) => () => void,
  handleDelete: (id: string) => () => void,
  can: (permission: PermissionsOfAdmin) => boolean,
  handlePress?: (id: string) => () => void,
  selectedIds: Array<string> = [],
) =>
  data?.map((item) => {
    const {
      type,
      sentDate,
      subject,
      message,
      broadcastGroups,
      broadcastMessageId,
    } = item ?? {};
    const date = getDayMonthDateYear(sentDate);
    const time = getHourMinute(sentDate, timeFormat);

    const canUseBroadcast = can(BroadcastNotification);

    const timeString = `${date} | ${time}`;
    const groupName = broadcastGroups?.map((inItem) =>
      inItem.name).join('; ');

    const value = [{ key: '2', label: strings.message, value: message }];

    if (subject) {
      value.unshift({ key: '1', label: strings.subject, value: subject });
    }

    return {
      key: broadcastMessageId,
      data: [
        {
          key: '1',
          value: BroadcastNotificationMap[type],
          type: text,
        },
        {
          key: '2',
          value: timeString,
          type: text,
        },
        {
          key: '3',
          value,
          type: labelValue,
        },
        { key: '4', value: groupName, type: text },
        {
          key: '5',
          value: [
            canUseBroadcast && {
              key: '1',
              icon: editIcon,
              tooltip: strings.edit,
              onClick: handleEdit(broadcastMessageId, type),
            },
            canUseBroadcast && {
              key: '2',
              icon: deleteIcon,
              tooltip: strings.delete,
              onClick: handleDelete(broadcastMessageId),
            },
          ],
          type: bar,
        },
        canUseBroadcast && !!handlePress && {
          key: '6',
          value: {
            checked: selectedIds.includes(broadcastMessageId),
            handlePress: handlePress(broadcastMessageId),
          },
          isCenterAligned: true,
          type: checkbox,
        },
      ],
    };
  });
